import { useCallback, useMemo, useState } from "react";

import {
    Column,
    EmptyPlaceholder,
    MEDIA,
    MediaStack,
    MediaWrapper,
    SearchInput,
    ToggleSpacedButtons,
    hideScrollbarSx,
    repeatElement
} from "@bridgesplit/ui";
import { DEFAULT_PRINCIPAL_MINT, filterTruthy, isAll, textContains } from "@bridgesplit/utils";
import { getSearchFromMetadata, useTokenListContext } from "@bridgesplit/abf-react";
import { TokenListTag } from "@bridgesplit/abf-sdk";

import CollateralMarketsTable from "./CollateralMarketsTable";
import CollateralMarketCard from "./CollateralMarketCard";
import { useCollateralMarkets } from "./util";
import { CollateralMarket } from "./types";
import {
    getTokenTagMetadata,
    PageSubtitle,
    TableCardWrapper,
    TOKEN_TAG_FILTER_ALL,
    TokenAutoSelect,
    TokenTagFilter
} from "../../common";

const DEFAULT_FILTER = TokenListTag.Featured;
const options: TokenTagFilter[] = [
    TokenListTag.Featured,
    TOKEN_TAG_FILTER_ALL,
    TokenListTag.DeFi,
    TokenListTag.LP,
    TokenListTag.Memes,
    TokenListTag.LST,
    TokenListTag.Governance,
    TokenListTag.xStock
];
const tagOptions = options.map(getTokenTagMetadata).filter(filterTruthy);
const QUERY = MEDIA.LG;
export default function CollateralMarkets() {
    const [principalMint, setPrincipalMint] = useState<string | undefined>(DEFAULT_PRINCIPAL_MINT);
    const { data: rawData, isLoading } = useCollateralMarkets(principalMint ?? DEFAULT_PRINCIPAL_MINT);
    const [filterState, setFilter] = useState<TokenTagFilter>();
    const filter = filterState ?? DEFAULT_FILTER;
    const { principalTokens } = useTokenListContext();
    const [search, setSearch] = useState<string>("");
    const data = useMemo(() => {
        if (!rawData) return rawData;
        return rawData.filter(({ metadata }) => {
            if (!search) {
                const tagFilter = isAll(filter) || metadata.tags?.includes(filter);
                return tagFilter;
            }
            if (metadata.mint === search) return true;
            return textContains(getSearchFromMetadata(metadata), search);
        });
    }, [filter, rawData, search]);

    const setTokenMint = useCallback((mint: string | undefined) => {
        setPrincipalMint(mint);
        setSearch("");
        setFilter(TOKEN_TAG_FILTER_ALL);
    }, []);

    const handleSearchChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        setSearch(e.target.value);
        if (e.target.value) {
            setFilter(TOKEN_TAG_FILTER_ALL);
        }
    }, []);

    return (
        <Column spacing={3}>
            <PageSubtitle>Collateral</PageSubtitle>
            <MediaStack spacing={1}>
                <SearchInput onChange={handleSearchChange} value={search} placeholder="Search" fullWidth />
                <TokenAutoSelect
                    mint={principalMint}
                    setMint={setTokenMint}
                    tokens={principalTokens}
                    disableClearable
                />
            </MediaStack>

            <ToggleSpacedButtons
                variant="contained"
                containerSx={{ overflowX: "auto", ...hideScrollbarSx }}
                size="small"
                value={filter}
                setValue={(value) => {
                    setFilter(value);
                    setSearch("");
                }}
                options={tagOptions.map((o) => ({
                    value: o.tag,
                    label: (
                        <>
                            {o.icon} {o.label}
                        </>
                    )
                }))}
            />

            <Mobile markets={data} />
            <MediaWrapper query={QUERY.above}>
                <TableCardWrapper>
                    <CollateralMarketsTable key={filter} markets={data} marketsLoading={isLoading} />
                </TableCardWrapper>
            </MediaWrapper>
        </Column>
    );
}

function Mobile({ markets }: { markets: CollateralMarket[] | undefined }) {
    return (
        <Column sx={{ [QUERY.above]: { display: "none" }, width: "100%" }} spacing={2}>
            {markets?.length === 0 && (
                <EmptyPlaceholder
                    header="No eligible collateral"
                    description="You don't own any Loopscale-supported collateral"
                />
            )}
            {markets?.map((market) => (
                <CollateralMarketCard key={market.metadata.mint} market={market} />
            ))}
            {!markets && repeatElement(<CollateralMarketCard market={undefined} />)}
        </Column>
    );
}
